import { useIsUserRole } from "@/app/utils/auth";
import {
  InformationCircleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/16/solid";
import React, { useState } from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";
import Header from "../../components/header/Header";
import { useGetCourseQuery } from "../../services/courseApi";
import { useGetTopicsQuery } from "../../services/quizApi";
import { showToast } from "../../slices/statusSlice";
import { buildQueryString } from "../../utils/api.util";

const QuizPage: React.FC = () => {
  type TopicsType = {
    string: string;
    isActive: boolean;
    showInSearch: boolean;
  };

  const navigate = useNavigate();

  const { courseId } = useParams();
  if (!courseId) {
    throw new Error("No courseId provided");
  }
  const { data: course } = useGetCourseQuery(courseId ?? "");

  const [isManaging, setIsManaging] = useState(false);
  const isAdmin = useIsUserRole("admin");

  useEffect(() => {
    setIsManaging(course?.isManaging || isAdmin);
  }, [course?.isManaging, isAdmin]);

  const { data: topicsData } = useGetTopicsQuery({ courseId });

  const dispatch = useDispatch();

  const [searchInput, setSearchInput] = useState<string>("");

  const [topics, setTopics] = useState<TopicsType[]>([]);

  const [selectedTopic, setSelectedTopic] = useState<string>("");

  const { t } = useTranslation(["quizPage", "toast", "coursePage"]);

  useEffect(() => {
    if (topicsData) {
      setTopics(
        topicsData.topics.map((topic) => ({
          string: topic,
          isActive: false,
          showInSearch: true,
        })),
      );
    }
  }, [topicsData]);

  const showNoTopic = () => {
    dispatch(
      showToast({
        message: t("warningNoTopic"),
        type: "error",
      }),
    );
    return;
  };

  const selectTopic = (index: number, topic: TopicsType) => {
    const newTopics = [...topics];
    newTopics[index].isActive = !newTopics[index].isActive;
    setTopics(newTopics);

    if (topic.string === selectedTopic) {
      setSelectedTopic("");
    } else {
      setSelectedTopic(topic.string);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setTopics((prevTopics) => {
      return prevTopics.map((topic) => {
        return {
          ...topic,
          showInSearch: topic.string
            .toLowerCase()
            .includes(e.target.value.toLowerCase()),
        };
      });
    });
  };

  return (
    <Header
      bottomPadding={false}
      breadcrumbs={[
        {
          name:
            course?.name ?? t("breadcrumbFallback.course", { ns: "header" }),
          link: `/course/${courseId}`,
        },
        {
          name: t("quiz"),
        },
      ]}
    >
      <div className="flex h-[calc(100vh-190px)] flex-col items-center justify-center sm:h-[calc(100vh-150px)]">
        <div className="flex w-full flex-col rounded-box bg-neutral p-6 lg:w-1/2">
          {topics.length > 0 ? (
            <>
              <div className="flex w-20">
                <h2 className="my-sm flex items-center font-bold text-xl">
                  {t("topics")}
                </h2>
              </div>

              <label className="input input-sm sm:input-md input-bordered mb-sm flex max-w-96 items-center rounded-xl ">
                <input
                  type="text"
                  className="grow"
                  placeholder="Search"
                  value={searchInput}
                  onChange={handleSearch}
                />
                <MagnifyingGlassIcon className="h-6 w-6 sm:h-7 sm:w-7 " />
              </label>
              <div className="scrollbar-thin scrollbar-thumb-rounded scrollbar-thumb-gray-400 h-[40vh] gap-y-2 overflow-y-scroll sm:h-[30vh]">
                {topics
                  .filter((topic) => topic.showInSearch)
                  .map((topic, index) => (
                    <button
                      type="button"
                      key={index}
                      className={`btn btn-sm sm:btn-md my-1 mr-2 h-auto items-center rounded-2xl font-bold text-sm ${
                        topic.string === selectedTopic
                          ? "btn-primary"
                          : "border-2 hover:border-primary"
                      }`}
                      onClick={() => selectTopic(index, topic)}
                    >
                      {topic.string}
                    </button>
                  ))}
              </div>
              <div className="mt-lg flex w-full justify-center">
                <button
                  type="button"
                  className="btn btn-primary min-w-40"
                  onClick={() => {
                    if (selectedTopic !== "" && selectedTopic !== null) {
                      const path = buildQueryString("session", {
                        topic: selectedTopic,
                      });
                      navigate(path);
                    } else {
                      showNoTopic();
                    }
                  }}
                >
                  {t("startQuiz")}
                </button>
              </div>
            </>
          ) : isManaging ? (
            <div className="flex w-full flex-col items-center justify-center gap-y-6">
              <div className="flex flex-row items-center gap-x-4">
                <InformationCircleIcon className="h-8 w-8 text-primary" />
                <span className="font-bold ">
                  {t("infoNoQuestionsLecturer")}
                </span>
              </div>
              <button
                type="button"
                className="btn btn-primary animate-pulse"
                onClick={() => navigate("../manage/quiz")}
              >
                {t("generateQuestions", { ns: "coursePage" })}
              </button>
            </div>
          ) : (
            <div className="flex w-full flex-col items-center justify-center gap-y-6">
              <div className="flex flex-row items-center gap-x-4">
                <InformationCircleIcon className="h-8 w-8 text-primary" />
                <span className="font-bold ">
                  {t("infoNoQuestionsStudent")}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </Header>
  );
};

export default QuizPage;
